import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getUserClientProjects, getDashboard } from '@/shared/api/base-api';
import { UserClientProjectsData } from '@/types/base';

// Query key factory
export const userProjectsKeys = {
  all: ['user-projects'] as const,
  clientProjects: (clientName?: string) => 
    clientName 
      ? [...userProjectsKeys.all, 'client-projects', clientName] as const
      : [...userProjectsKeys.all, 'client-projects'] as const,
};

// Cache configuration for user projects
const CACHE_CONFIG = {
  staleTime: 5 * 60 * 1000, // 5 minutes - projects don't change frequently
  gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
  refetchOnWindowFocus: false, // Don't refetch when window gains focus
  refetchOnMount: false, // Don't refetch on component mount if data exists
  refetchOnReconnect: 'always' as const, // Refetch when internet reconnects
};

/**
 * Hook to fetch and cache client projects
 * @param clientName - Optional client name. If provided, fetches that client's projects. Otherwise, fetches the logged-in user's client projects.
 * This includes both direct access projects and invited projects
 */
export const useUserClientProjects = (clientName?: string) => {
  return useQuery<UserClientProjectsData>({
    queryKey: userProjectsKeys.clientProjects(clientName),
    queryFn: async () => {
      if (clientName) {
        // Fetch specific client's dashboard data
        const dashboardData = await getDashboard(clientName);
        // Transform to match UserClientProjectsData structure
        return {
          ...dashboardData,
          active_projects: dashboardData.projects.filter(p => p.is_active),
          completed_projects: dashboardData.projects.filter(p => !p.is_active),
        };
      } else {
        // Fetch logged-in user's client projects
        return getUserClientProjects();
      }
    },
    ...CACHE_CONFIG,
  });
};

/**
 * Helper function to check if a field has a valid value (not null, undefined, empty string, or 'None')
 */
const hasValidValue = (value: string | null | undefined): boolean => {
  return value !== null && 
         value !== undefined && 
         value.trim() !== '' && 
         value.trim().toLowerCase() !== 'none';
};

/**
 * Hook to get projects with progress directories
 * @param clientName - Optional client name to fetch projects for a specific client
 */
export const useProjectsWithProgress = (clientName?: string) => {
  const { data, ...rest } = useUserClientProjects(clientName);
  
  const progressProjects = React.useMemo(() => {
    if (!data) return [];
    
    const allProjects = [...(data.projects || []), ...(data.invited_projects || [])];
    return allProjects.filter(project => hasValidValue(project.progress_directory));
  }, [data]);
  
  return {
    progressProjects,
    data,
    ...rest,
  };
};

/**
 * Hook to get projects with asbuilds directories
 * @param clientName - Optional client name to fetch projects for a specific client
 */
export const useProjectsWithAsbuilds = (clientName?: string) => {
  const { data, ...rest } = useUserClientProjects(clientName);
  
  const asbuildsProjects = React.useMemo(() => {
    if (!data) return [];
    
    const allProjects = [...(data.projects || []), ...(data.invited_projects || [])];
    return allProjects.filter(project => hasValidValue(project.asbuilds_directory));
  }, [data]);
  
  return {
    asbuildsProjects,
    data,
    ...rest,
  };
};

/**
 * Hook to get projects with drones directories (Post Tension)
 * @param clientName - Optional client name to fetch projects for a specific client
 */
export const useProjectsWithDrones = (clientName?: string) => {
  const { data, ...rest } = useUserClientProjects(clientName);
  
  const dronesProjects = React.useMemo(() => {
    if (!data) return [];
    
    const allProjects = [...(data.projects || []), ...(data.invited_projects || [])];
    return allProjects.filter(project => hasValidValue(project.drones_directory));
  }, [data]);
  
  return {
    dronesProjects,
    data,
    ...rest,
  };
};

/**
 * Hook to get projects with drone photos links
 * @param clientName - Optional client name to fetch projects for a specific client
 */
export const useProjectsWithDronePhotos = (clientName?: string) => {
  const { data, ...rest } = useUserClientProjects(clientName);
  
  const dronePhotosProjects = React.useMemo(() => {
    if (!data) return [];
    
    const allProjects = [...(data.projects || []), ...(data.invited_projects || [])];
    return allProjects.filter(project => hasValidValue(project.drone_photos_link));
  }, [data]);
  
  return {
    dronePhotosProjects,
    data,
    ...rest,
  };
};

/**
 * Hook to get just the active projects for the user's client
 * @param clientName - Optional client name to fetch projects for a specific client
 * @deprecated Use specific content-based hooks instead
 */
export const useUserActiveProjects = (clientName?: string) => {
  const { data, ...rest } = useUserClientProjects(clientName);
  
  return {
    activeProjects: data?.active_projects || [],
    data,
    ...rest,
  };
};

/**
 * Hook to get just the completed projects for the user's client
 * @param clientName - Optional client name to fetch projects for a specific client
 * @deprecated Use specific content-based hooks instead
 */
export const useUserCompletedProjects = (clientName?: string) => {
  const { data, ...rest } = useUserClientProjects(clientName);
  
  return {
    completedProjects: data?.completed_projects || [],
    data,
    ...rest,
  };
};

/**
 * Hook to get project counts
 * @param clientName - Optional client name to fetch projects for a specific client
 */
export const useUserProjectCounts = (clientName?: string) => {
  const { data, ...rest } = useUserClientProjects(clientName);
  
  return {
    activeCount: data?.active_count || 0,
    completedCount: data?.completed_count || 0,
    totalCount: (data?.active_count || 0) + (data?.completed_count || 0),
    data,
    ...rest,
  };
};

/**
 * Hook to get projects with surveillance enabled
 * @param clientName - Optional client name to fetch projects for a specific client
 */
export const useProjectsWithSurveillance = (clientName?: string) => {
  const { data, ...rest } = useUserClientProjects(clientName);
  
  const surveillanceProjects = React.useMemo(() => {
    if (!data) return [];
    
    const allProjects = [...(data.projects || []), ...(data.invited_projects || [])];
    return allProjects.filter(project => project.has_surveillance);
  }, [data]);
  
  return {
    surveillanceProjects,
    data,
    ...rest,
  };
};

/**
 * Hook to get all user projects (for team management, etc.)
 * @param clientName - Optional client name to fetch projects for a specific client
 */
export const useAllUserProjects = (clientName?: string) => {
  const { data, ...rest } = useUserClientProjects(clientName);
  
  const allProjects = React.useMemo(() => {
    if (!data) return [];
    return [...(data.projects || []), ...(data.invited_projects || [])];
  }, [data]);
  
  return {
    allProjects,
    data,
    ...rest,
  };
};
